const rebateTable = [
    {
        range: [0, 65],
        tiers: [27.820, 18.547, 9.273, 0]
    }, {
        range: [65, 70],
        tiers: [32.457, 23.184, 13.910, 0]
    }, {
        range: [70, 200],
        tiers: [37.094, 27.820, 18.547, 0]
    }
];

/**
 * Get age for rebate purposes (age as at end of current financial year)
 */
export function calculateRebateAge({ age, dob }) {
    return age || calculateAge(dob);
}

/**
 * Age for rebate calculations will be the greater of the two 'rebate ages' on the application (personal + partner)
 * @param applicant: AonApplicationService application record
 * @return {*}
 */
export function calculateCombinedRebateAge(applicant) {
    let age = calculateRebateAge(applicant.personal);

    // If a partner is specified, use the oldest of the two ages for rebate purposes
    if (applicant.hasPartner() && _.get(applicant, 'partner.dob', false)) {
        const partnerAge = calculateRebateAge(applicant.partner);
        age = Math.max(age, partnerAge);
    }

    return age;
}

export function calcRebate(applicant, AonApplicationService) {
    if (!applicant.policy.federal_rebate) {
        return 0;
    }

    const age = calculateCombinedRebateAge(applicant, AonApplicationService);
    let tier = applicant.policy.rebate_tier;

    // The rebate tier matrix is used in 'operator' mode, operator explicitly
    // selects the age range
    if (_.get(applicant.policy, "rebate_tier_matrix", false) !== false) {
        return rebateTable[applicant.policy.rebate_tier_age_index].tiers[tier] / 100;
    }

    for (let i = 0; i < rebateTable.length; i++) {
        if (age >= rebateTable[i].range[0] && age < rebateTable[i].range[1]) {
            return rebateTable[i].tiers[tier] / 100;
        }
    }
};

export function doesLHCApply(dob) {
    // If born before Jul 1, 1934, there is no LHC
    let born = new Date(dob);
    let cutoff = new Date("1934-07-01");
    if ((cutoff.getTime() > born.getTime())) {
        return false;
    }

    const age = calculateAge(dob, {financialYear: true});

    if (age > 31) {
        return true
    }
    else if(age == 31){
        const now = getCurrentDate()
        const yearDiff = Math.abs(born.getUTCFullYear() -  now.getFullYear())

        if (born.getMonth()+1 <= 6){
            return true
        }
        else if (yearDiff == 32){
            return true
        }
        else {
            return false
        }
    }
    else {
        return false;
    }
};

export function getLastFinancialYearEndDate() {
    const now = new Date();
    const isAfterJuly1st = now.getMonth() >= 6 && now.getDate() >= 1;
    const finYear = now.getFullYear() - (isAfterJuly1st ? 0 : 1);
    return new Date(finYear, 6, 1, 0, 0, 0);
}

export function getCurrentDate() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 0, 0);
}

export function calculateAge(dob, options = { financialYear: true } ) {
    if (!dob) {
        return null;
    }
    const now = options.financialYear ? getLastFinancialYearEndDate() : getCurrentDate();
    let [year, month, day] = dob.split('-');
    const birthDate = new Date(year, month - 1, day, 0, 0, 0);
    const ageDiffMs = now - birthDate.getTime();
    const ageDate = new Date(ageDiffMs); // miliseconds from epoch

    return Math.abs(ageDate.getUTCFullYear() - 1970);
};

//Function to display what their LHC Age would be based on the LHC Base day calculation. Used for Frontend display only,
// for LHC calculcation see: CalculateAge / DoesLHCApply()
export function calculateLHCAge(dob) {

    let age = calculateAge(dob, {financialYear: false})
    if (age == null){
        return null
    }
    let[year, month, day] = dob.split('-');
    const birthDate = new Date(year, month -1, day, 0, 0, 0);
    const now = getCurrentDate()
    const yearDiff = Math.abs(birthDate.getUTCFullYear() - now.getUTCFullYear());
    let baseDate;
    if (age == 31) {
        if (yearDiff != 32) {
            if (birthDate.getMonth() + 1 >= 7) {
                baseDate = new Date(now.getFullYear() + 1, 7, 1, 0, 0, 0)
            } else {
                baseDate = new Date(now.getFullYear(), 7, 1, 0, 0, 0)
            }
        } else {
            baseDate = new Date(now.getFullYear(), 7, 1, 0, 0, 0)
        }
        if(now < baseDate){
            age -= 1;
        }
    }
    age = age > 65 ? 65 : age ;
    return age;
};

export function calcLHC(applicant, AonApplicationService) {

    let result = {
        totalLHC: 0,
        primaryLHC: 'N/A',
        primaryAge: 'N/A',
        partnerLHC: 'N/A',
        partnerAge: 'N/A'
    };

    // LHC does not apply to only extras cover
    if (applicant.policy.cover_type == 1) {
        return result;
    }

    const ageToLHC = age => Math.min((Math.max(0, age - 30)) * 0.02, 0.7);

    // If LHC age is supplied, use that instead of DOB:
    const supplied_lhc_age = _.get(applicant, "policy.lhc_age", null);
    const supplied_lhc_age_partner = _.get(applicant, "policy.lhc_age_partner", null);
    const primaryAge = supplied_lhc_age ? supplied_lhc_age : (applicant.personal.age || calculateAge(applicant.personal.dob));
    const primaryLHC = doesLHCApply(applicant.personal.dob) ? ageToLHC(primaryAge) : 0;
    result.primaryAge = primaryAge;
    result.primaryLHC = primaryLHC;
    let totalLHC = primaryLHC;
    if (applicant.hasPartner()) {
        // Average the loading between the primary and partner
        const partnerAge = (supplied_lhc_age_partner !== null) ? supplied_lhc_age_partner : (applicant.partner.age || calculateAge(applicant.partner.dob));
        const partnerLHC = doesLHCApply(applicant.partner.dob) ? ageToLHC(partnerAge) : 0;
        totalLHC = (partnerLHC + primaryLHC) / 2;
        result.partnerAge = partnerAge;
        result.partnerLHC = partnerLHC;
    }

    result.totalLHC = totalLHC;
    return result;
};

// For any price package that has direct debit discount available, apply the discount
export function applyDirectDebitDiscount(pricePackage) {
    pricePackage.hasDirectDebitDiscount = false;
    for (let price of pricePackage.prices) {
        // If DD discount is available, the discounts on the DD pricing will be more than normal pricing
        const pricing = price.pricing.available;
        if (pricing) {
            const {
                monthly_adjusted_price: {discounts},
                monthly_adjusted_price_dd_discount: {discounts: dd_discounts}
            } = pricing;
            const hasDirectDebitDiscount = dd_discounts.length > discounts.length;
            if (hasDirectDebitDiscount) {
                pricePackage.hasDirectDebitDiscount = true;
                pricePackage.apply_direct_debit_discount = true;
                return;
            }
        }
    }
}

// Adds a 'calculations' field to each price in a price package, containing LHC, rebate, total, etc
export function priceCalculations(pricePackage, rebatePercentage, healthCoverLoading, paymentFrequency) {
    for (let i = 0; i < pricePackage.prices.length; i++) {
        let premium = parseFloat(pricePackage.prices[i].gross_monthly_full_premium);
        let lhcBase = parseFloat(pricePackage.prices[i].gross_monthly_lhc);

        if(paymentFrequency !== null && paymentFrequency === 'fortnightly'){
            premium = parseFloat(pricePackage.prices[i].gross_fortnightly_full_premium);
            lhcBase = parseFloat(pricePackage.prices[i].gross_fortnightly_lhc);
        }

        if (lhcBase === 0) {
            lhcBase = premium;
        }

        // Include discounts
        let discounted_premium = premium;
        let calculations = {
            discounts: [],
        };
        const dd_discount = _.get(pricePackage, 'apply_direct_debit_discount', false);

        for(let d = 0; d < pricePackage.discounts.length; d++) {
            let discount = pricePackage.discounts[d];
            if (discount.is_direct_debit && !dd_discount) {
                continue;
            }

            let discountDollars = 0;
            if (discount.is_absolute) {
                discountDollars = discount.value;
            } else {
                // Percentage discount
                discountDollars = (discount.value/100) * premium;
            }
            discounted_premium -= discountDollars;
            // Track discounts which are applied
            discount.dollarAmount = discountDollars;
            calculations.discounts.push(discount);
        }

        calculations.paymentFrequency = paymentFrequency || 'monthly';
        calculations.basePremium = premium;
        calculations.discountedPremium = discounted_premium;
        calculations.rebatePercent = rebatePercentage * 100;
        calculations.rebateAmount = discounted_premium * rebatePercentage;
        calculations.priceWithRebate = discounted_premium - calculations.rebateAmount;
        calculations.lhcPercent = healthCoverLoading.totalLHC * 100;
        calculations.lhcAmount = lhcBase * healthCoverLoading.totalLHC;
        calculations.lhcBreakdown = 'Primary: ' + (healthCoverLoading.primaryLHC !== 'N/A' ? (healthCoverLoading.primaryLHC * 100) + '%' : healthCoverLoading.primaryLHC) +
        ' Partner: ' + (healthCoverLoading.partnerLHC !== 'N/A' ? (healthCoverLoading.partnerLHC * 100) + '%' : healthCoverLoading.partnerLHC);
        calculations.priceWithLHC = discounted_premium + calculations.lhcAmount;
        calculations.totalPrice = discounted_premium + calculations.lhcAmount - calculations.rebateAmount;
        pricePackage.prices[i].calculations = calculations;
    }
};

export function splitServicesIntoColumns(services) {
    const splitToKeys = (col, keys) => {
        const parts = _.chunk(col, Math.ceil(col.length / keys.length));
        return _.zipObject(keys, parts);
    };

    const visible = service => service.visible;

    return {
        hospital: splitToKeys(
          services.hospital.filter(visible).sort((a, b) => b.sort_order - a.sort_order), ['left', 'right']
        ),
        ancillary: splitToKeys(
          services.ancillary.filter(visible).sort((a, b) => b.sort_order - a.sort_order), ['left', 'right']
        ),
    };
}

// Copy the applicant data, and add in DOB's calculated from the rebate tier index
// if required (corporate can be configured to not capture DOB or AGE)
// This is done to simplify pricing calculation assumptions in the backend
export function applicantWithDOB(applicant) {
    let applicantData = _.cloneDeep(applicant);
    const federalRebate = _.get(applicantData, 'policy.federal_rebate', false);
    const rebateTierAgeIndex = _.get(applicantData, 'policy.rebate_tier_age_index', 0);
    if ((_.get(applicantData, 'personal.dob', null) === null) && federalRebate) {
        const dobFromAge = age => {
            let dob = new Date();
            dob.setYear(dob.getFullYear() - age);
            return [dob.getFullYear(), dob.getMonth() + 1, dob.getDate()].join("-");
        };
        const rebateTierAge = [30, 67, 80];
        applicantData.personal.dob = dobFromAge(rebateTierAge[rebateTierAgeIndex]);
    }
    return applicantData;
};

/**
 * Takes a price package (returned from package search api for example), a list of services,
 * and the services associated with a price, and returns a map of service id to the matching
 * service on the price.
 * @param pricePackage
 * @param services
 * @param productServices
 */
export function getProductFeatures(pricePackage, services, productServices) {
    let productFeatures = {};
    for (let feature of services) {
        productFeatures[feature.id] = _(productServices)
            .filter(productService => productService.service === feature.id)
            .first();
    }
    return productFeatures;
}


/**
 * Return the payment frequency key in AonConfig.jsConstants.PaymentFrequencies
 * given the numeric value
 * @param frequency number Numeric payment frequency (see AonConfig.jsConstants.PaymentFrequencies)
 */
export function getPaymentFrequencyKey(frequency) {
    const jsConstants = window.__APP_CONTEXT__.jsConstants;
    return Object.keys(jsConstants.PaymentFrequencies).find(
        key => jsConstants.PaymentFrequencies[key] === frequency
    );
}

/**
 * Given a key into the payment frequency config, return a normalized
 * string value that can be used as <select> option values
 * @param key
 * @return {string}
 */
export function getPaymentFrequencyOptionFromKey(key) {
    return key.toLowerCase().replace(/[^a-z]/, '')
}

/**
 * Get pricing based on current/future, direct debit, payment frequency, etc.
 * @param pkg The price package to get pricing for
 * @param which 'available'|'future' Current or future pricing
 * @param frequency number Numeric payment frequency (see AonConfig.jsConstants.PaymentFrequencies)
 * @param directDebitDiscount true|false Whether to apply DD discount
 * @param noDiscounts true|false Whether to pull from the no discounts pricing
 */
export function getPricingForFrequency(pkg, which, frequency, directDebitDiscount, noDiscounts) {
    if (!pkg.selectedPrice.pricing.hasOwnProperty(which)) {
        return null;
    }

    // Convert a numeric payment frequency into a key into the pricing information structure
    const paymentFrequency = getPaymentFrequencyOptionFromKey(getPaymentFrequencyKey(frequency));
    const pricingKey = `${paymentFrequency}_adjusted_price`;
    const pricingKeySuffix = directDebitDiscount
        ? '_dd_discount'
        : noDiscounts
            ? '_no_discounts'
            : '';
    const pricing = pkg.selectedPrice.pricing[which];
    const finalPricingKey = `${pricingKey}${pricingKeySuffix}`;
    return pricing[finalPricingKey];
}

export function getDiscountCodeRestrictions(code) {
    const formChoices = window.__APP_CONTEXT__.formChoices;
    return formChoices.discount_code_restrictions[code];
}

/**
 * If a discount code has been selected which limits form choices, apply those limits here.
 * Note that this will mutate the fieldsets dictionary.
 */
export function restrictFieldSets(applicant, fieldSets) {
    const selectedDiscount = _.get(applicant, 'policy.discount_code');
    const restrictions = getDiscountCodeRestrictions(selectedDiscount);
    if (restrictions) {
        const frequencyRestriction = restrictions.payment_frequency;
        if (Number.isFinite(frequencyRestriction)) {
            _.set(fieldSets, 'payment.frequency.choices', [
              [frequencyRestriction, getPaymentFrequencyKey(frequencyRestriction)],
            ]);
        }
    }
}

/**
 * The debug link to pricing summary information requires the current applicant
 * data in order to pass it along to the fetch price endpoint, to get accurate
 * pricing. We store that in local storage (just a copy of the current applicant struct).
 */
export function savePriceSummaryLinkInformation(application) {
    window.localStorage.setItem('price-summary-application', JSON.stringify(application));
}


// WEBPACK FOOTER //
// ./src/utility/comparator.js